import { createStore } from "zustand/vanilla";
import { ok } from "@pack-it-up/shared";
import { planParticipantRemoval, previewParticipantRemoval, } from "@pack-it-up/core";
import { errorToMessage } from "../errorToMessage.js";
export const createTemplatesStore = (deps) => {
    // Stream subscriptions are owned by the store: dispose() unsubscribes them all, so a
    // superseded store stops writing into state nothing renders. `subs` is filled after
    // the store exists (the streams push into it).
    const subs = [];
    const store = createStore((set) => ({
        templates: [],
        searchResults: null,
        error: null,
        async search(query) {
            if (query.trim() === "") {
                set({ searchResults: null });
                return;
            }
            const r = await deps.templates.searchTemplates(deps.userId, query);
            if (r.ok)
                set({ searchResults: r.value, error: null });
            else
                set({ error: errorToMessage(r.error) });
        },
        async deleteTemplate(templateId) {
            const r = await deps.templates.deleteTemplate(templateId);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async addUserParticipant(templateId, user) {
            const r = await deps.templates.addUserParticipant(templateId, user);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async addManagedParticipant(templateId, displayName) {
            const r = await deps.templates.addManagedParticipant(templateId, displayName);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        previewParticipantRemoval: (participantId, rows) => previewParticipantRemoval(participantId, rows),
        async removeParticipant(templateId, participantId, rows) {
            // Rows first: while the participant still exists, a row that loses its last
            // allocation is visibly theirs. The plan is a no-op when no row named them.
            const cleanup = await deps.applyParticipantRemovalPlan(planParticipantRemoval(participantId, rows));
            if (!cleanup.ok) {
                set({ error: errorToMessage(cleanup.error) });
                return cleanup;
            }
            const r = await deps.templates.removeParticipant(templateId, participantId);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async archiveTemplate(templateId) {
            const r = await deps.templates.archiveTemplate(templateId, true);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async renameTemplate(templateId, name) {
            const trimmed = name.trim();
            // An emptied title is a mis-edit, not a request to blank the name (Doc 06 §4).
            if (trimmed === "")
                return ok(undefined);
            const r = await deps.templates.renameTemplate(templateId, trimmed);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async setTemplateColor(templateId, color) {
            const r = await deps.templates.setTemplateColor(templateId, color);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async setTemplateEmoji(templateId, emoji) {
            const r = await deps.templates.setTemplateEmoji(templateId, emoji);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        dispose() {
            for (const unsubscribe of subs.splice(0))
                unsubscribe();
        },
    }));
    subs.push(deps.templates
        .observeMyTemplates(deps.userId, { archived: deps.archived ?? false })
        .subscribe((templates) => store.setState({ templates })));
    return store;
};
//# sourceMappingURL=templatesStore.js.map